import {noop} from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {OkCancelDialog} from '../../../../../../libs/shared/ui/src/components/dialogs/okCancelDialog';
import {Icon, IconName} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {PermissionCheck} from '../../../../../../srcCommon/permissions/permissionCheck';
import {PermissionDenied} from '../../../../../../srcCommon/permissions/permissionDenied';
import {PermissionResult, yesPermission} from '../../../../../helpers/permissionHelpers';

/*
 * Props.
 */

export interface CommentOperationsProps {
  canRetry?: boolean;
  retryComment?: () => void;
  editComment?: () => void;
  deleteComment?: () => void;
  replyToComment?: () => void;
  togglePinComment?: () => void;
  isPinned?: boolean;
  isVisibleOnlyToUser?: boolean;
}

/*
 * Style.
 */

const StyledOperationsDiv = styled.div`
  display: flex;
  align-items: center;
`;

interface OperationStyleProps {
  isDisabled?: boolean;
}

const operationStyle = css<OperationStyleProps>`
  height: 30px;
  border-radius: 50%;
  background: transparent;
  padding: 7px;
  margin: 0 -2px;

  ${(p) => maybeAddOperationHoverStyle(p.isDisabled)};
`;

function maybeAddOperationHoverStyle(isDisabled = false) {
  // No hover when disabled.
  if (isDisabled) {
    return undefined;
  }

  return css`
    &:hover {
      background: ${(p) => p.theme.alphas.gray10};
    }
  `;
}

const StyledOperationButton = styled(ChromelessButton)`
  ${operationStyle};
`;

// TODO: Refactor PopoverCoordinator so we have direct control over the button and don't have to do this.
const StyledOperationPopoverCoordinator = styled(PopoverCoordinator)`
  ${operationStyle};
`;

/*
 * Component.
 */

export const CommentOperations: FC<CommentOperationsProps> = (props) => (
  <StyledOperationsDiv>
    {renderPinButton(props)}
    {renderReplyButton(props)}
    {renderMainOperation(props)}
    {renderDeleteButton(props)}
  </StyledOperationsDiv>
);

/*
 * Display retry or edit button.
 */

function renderMainOperation(props: CommentOperationsProps) {
  if (props.canRetry) {
    return renderRetryButton(props.retryComment);
  }

  if (props.editComment) {
    return renderEditButton(props.editComment);
  }

  return null;
}

/*
 * Reply button.
 */

function renderReplyButton(props: CommentOperationsProps) {
  if (!props.replyToComment) {
    return null;
  }

  return (
    <CommentOperationButton
      iconName="replySmall"
      tooltip={<Trans id="comment-operations-reply">Reply</Trans>}
      onClick={props.replyToComment}
    />
  );
}

/*
 * Retry button.
 */

function renderRetryButton(retryComment?: () => void) {
  return (
    <CommentOperationButton
      iconName="retrySmall"
      tooltip={<Trans id="comment-operations-retry">Retry</Trans>}
      onClick={retryComment}
    />
  );
}

/*
 * Edit button.
 */

function renderEditButton(editComment?: () => void) {
  return (
    <CommentOperationButton
      iconName="editSmall"
      tooltip={<Trans id="comment-operations-edit">Edit</Trans>}
      onClick={editComment}
    />
  );
}

/*
 * Pin button.
 */

const pinButtonIconName = 'pinOutlineSmall';
const unpinButtonIconName = 'unpinOutlineSmall';

// Exporting these since icon names (which double as test IDs) are liable to change.
export const pinButtonTestId = pinButtonIconName;
export const unpinButtonTestId = unpinButtonIconName;

function renderPinButton(props: CommentOperationsProps) {
  if (!props.togglePinComment) {
    return null;
  }

  const iconName = props.isPinned ? unpinButtonIconName : pinButtonIconName;
  const tooltip = props.isPinned ? (
    <Trans id="comment-operations-unpin">Unpin</Trans>
  ) : (
    <Trans id="comment-operations-pin">Pin</Trans>
  );

  return (
    <PermissionCheck>
      {(permissionResult) => (
        <CommentOperationButton
          permissionResult={permissionResult}
          iconName={iconName}
          tooltip={tooltip}
          onClick={props.togglePinComment}
          interactionId="pinOrUnpinButton"
        />
      )}
    </PermissionCheck>
  );
}

/*
 * Delete button.
 */

function renderDeleteButton(props: CommentOperationsProps) {
  if (!props.canRetry && !props.deleteComment) {
    return null;
  }

  return (
    <TooltipCoordinator hideOnMouseDown render={() => <Trans id="comment-operations-delete">Delete</Trans>}>
      <StyledOperationPopoverCoordinator
        renderButton={() => <CommentOperationButtonIcon name="trashSmall" />}
        renderModal={(modalProps) => renderConfirmDeleteDialog(modalProps, props)}
      />
    </TooltipCoordinator>
  );
}

/*
 * Delete confirmation dialog.
 */

function renderConfirmDeleteDialog(modalProps: PopoverTransferProps, props: CommentOperationsProps) {
  const {deleteComment, isPinned, isVisibleOnlyToUser} = props;

  return (
    <OkCancelDialog
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...modalProps}
      title={<Trans id="comment-delete-title">Delete comment</Trans>}
      okButtonLabel={<Trans id="comment-delete-ok">Delete</Trans>}
      okButtonVariant={ButtonVariantsEnum.DANGER}
      onOkClick={deleteComment ?? noop}
      isOkDisabled={!deleteComment}
    >
      {isVisibleOnlyToUser ? renderPrivateDeleteText(isPinned) : renderTeammateDeleteText(isPinned)}
    </OkCancelDialog>
  );
}

function renderPrivateDeleteText(isPinned?: boolean) {
  if (isPinned) {
    return (
      <Trans id="comment-delete-pinned-content">
        You will permanently delete this pinned comment. This action cannot be undone.
      </Trans>
    );
  }
  return (
    <Trans id="comment-delete-content">
      You will permanently delete this comment. This action cannot be undone.
    </Trans>
  );
}

function renderTeammateDeleteText(isPinned?: boolean) {
  if (isPinned) {
    return (
      <Trans id="comment-delete-team-pinned-content">
        You will permanently delete this pinned comment for all teammates. Everyone will see the comment as
        deleted.
      </Trans>
    );
  }
  return (
    <Trans id="comment-delete-team-content">
      You will permanently delete this comment for all teammates. Everyone will see the comment as deleted.
    </Trans>
  );
}

/*
 * Helper Button.
 */

interface CommentOperationButtonProps {
  permissionResult?: PermissionResult;
  iconName: IconName;
  tooltip: ReactNode;
  onClick?: () => void;
  interactionId?: string;
}

const CommentOperationButton: FC<CommentOperationButtonProps> = (props) => {
  const permissionResult = props.permissionResult || yesPermission();
  const permissionTooltip = permissionResult.isAllowed ? null : (
    <PermissionDenied reason={permissionResult.reason} />
  );

  const isDisabled = !permissionResult.isAllowed;
  const tooltip = permissionTooltip || props.tooltip;

  return (
    <TooltipCoordinator hideOnMouseDown render={() => tooltip}>
      <StyledOperationButton
        isDisabled={isDisabled}
        onClick={props.onClick}
        interactionId={props.interactionId}
      >
        <CommentOperationButtonIcon isDisabled={isDisabled} name={props.iconName} />
      </StyledOperationButton>
    </TooltipCoordinator>
  );
};

interface CommentOperationButtonIconProps {
  isDisabled?: boolean;
  name: IconName;
}

const CommentOperationButtonIcon: FC<CommentOperationButtonIconProps> = (props) => {
  const theme = useUserTheme();
  const baseColor = theme.greys.shade80;
  const color = props.isDisabled ? theme.greys.shade40 : baseColor;
  const hoverColor = props.isDisabled ? theme.greys.shade40 : theme.greys.shade80;

  return <Icon name={props.name} color={color} hoverColor={hoverColor} />;
};
